import { useState, useEffect } from "react";
import { AppLink, useAppNavigate } from "../lib/route-primitives";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useWedding } from "../contexts/wedding-context";
import { useAuth } from "../contexts/auth-context";
import { useApi } from "../hooks/use-api";
import { useActivityBadge } from "../hooks/use-activity-badge";
import { queryKeys } from "../lib/query-keys";
import { staleTimes } from "../lib/query-client";
import { getTopLiked, getActivity } from "../lib/api";
import type { TopLikedItem, ActivityItem } from "../lib/api";
import {
  Heart, ChevronRight, Images, Mail, Play,
  CheckCircle2, XCircle, HelpCircle, CalendarDays, MapPin,
  Upload, MessageCircle,
} from "lucide-react";
import { cn } from "../lib/utils";
import { cdnUrl } from "../lib/cdn";
import { paths } from "../lib/paths";
import { formatWeddingDate, WEDDING_DATE_LONG, listWeddingEvents, eventSourceFromRow, headlineEvent } from "@wedding/shared";
import type { ActiveWeddingEvent } from "@wedding/shared";
import { useActiveEvent, useEventName } from "../lib/wedding-event";

// ─── Helpers ─────────────────────────────────────────────────
// Cache Intl constructors per language — they're expensive to recreate
const rtfCache = new Map<string, Intl.RelativeTimeFormat>();

function formatRelativeTime(isoString: string, lang: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  try {
    let rtf = rtfCache.get(lang);
    if (!rtf) { rtf = new Intl.RelativeTimeFormat(lang, { numeric: "auto" }); rtfCache.set(lang, rtf); }
    const secs = Math.floor(diff / 1000);
    if (secs < 60) return rtf.format(-secs, "second");
    const mins = Math.floor(secs / 60);
    if (mins < 60) return rtf.format(-mins, "minute");
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return rtf.format(-hrs, "hour");
    return rtf.format(-Math.floor(hrs / 24), "day");
  } catch {
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m`;
    return `${Math.floor(mins / 60)}h`;
  }
}

// ─── Wedding Countdown ────────────────────────────────────────
// Hedef etkinliği çağıran seçer (`resolveActiveWeddingEvent`) — kına geçince
// sayaç kendiliğinden ana düğüne döner.
function WeddingCountdown({ event, title, bgCountdown }: { event: ActiveWeddingEvent; title: string; bgCountdown?: string | null }) {
  const { t } = useTranslation();
  const eventName = useEventName();
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const label = eventName(event);
  const target = new Date(event.dateTime).getTime();
  const diff = target - now;

  if (diff <= 0 && Math.abs(diff) < 86_400_000) {
    return (
      <div className="flex items-center justify-center gap-3 rounded-xl bg-primary px-4 py-5 text-center text-white">
        <span className="text-2xl">🎉</span>
        <div>
          <p className="font-serif font-bold text-lg italic">{title}</p>
          <p className="text-sm opacity-90">{t("home.countdown.todayFor", { event: label })}</p>
        </div>
        <span className="text-2xl">🎉</span>
      </div>
    );
  }

  if (diff > 0) {
    const totalSecs = Math.floor(diff / 1000);
    const days  = Math.floor(totalSecs / 86400);
    const hours = Math.floor((totalSecs % 86400) / 3600);
    const mins  = Math.floor((totalSecs % 3600) / 60);
    const secs  = totalSecs % 60;

    const units = [
      { val: days,                              unit: t("home.countdown.dayUnit") },
      { val: String(hours).padStart(2, "0"),    unit: t("home.countdown.hoursUnit") },
      { val: String(mins).padStart(2, "0"),     unit: t("home.countdown.minsUnit") },
      { val: String(secs).padStart(2, "0"),     unit: t("home.countdown.secsUnit") },
    ];

    return (
      <section className="relative overflow-hidden rounded-none md:rounded-xl group h-48 md:h-64 isolate -mx-4 -mt-4 md:mx-0 md:mt-0">
        {/* Background photo */}
        {bgCountdown && (
          <img
            src={cdnUrl(bgCountdown, { w: 1280 })}
            alt=""
            aria-hidden="true"
            width={1200}
            height={400}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 motion-reduce:transition-none"
          />
        )}
        {/* Overlay — lighter in light mode, darker in dark mode */}
        <div className="absolute inset-0 bg-[rgba(20,15,13,0.35)] dark:bg-[rgba(20,15,13,0.6)]" />
        <div className="relative h-full flex flex-col items-center justify-center text-center p-6 space-y-3 md:space-y-4">
          <div className="inline-block px-4 py-1 bg-primary/80 backdrop-blur-sm rounded-full text-xs font-bold uppercase tracking-widest text-white">
            {t("home.countdown.labelFor", { event: label })}
          </div>
          <h2 className="text-2xl md:text-4xl font-serif font-bold text-white tracking-tight">{title}</h2>
          <div className="flex gap-5 md:gap-8 text-white">
            {units.map(({ val, unit }, i, arr) => (
              <div key={unit} className="flex items-center gap-5 md:gap-8">
                <div className="text-center">
                  <p className="text-2xl md:text-3xl font-bold tabular-nums">{val}</p>
                  <p className="text-[10px] uppercase opacity-70 mt-0.5">{unit}</p>
                </div>
                {i < arr.length - 1 && (
                  <div className="text-xl md:text-3xl font-light text-white/30 leading-none -mt-2">:</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  const daysAgo = Math.floor(Math.abs(diff) / 86_400_000);
  return (
    <div className="flex items-center gap-4 rounded-xl border border-primary/10 bg-muted/40 px-5 py-4">
      <div className="flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-xl bg-muted text-muted-foreground">
        <span className="text-2xl font-bold leading-none">{daysAgo}</span>
        <span className="text-[10px] font-medium uppercase">{t("home.countdown.dayUnit")}</span>
      </div>
      <div className="min-w-0">
        <p className="truncate font-semibold">{title}</p>
        <p className="text-sm text-muted-foreground">{t("home.countdown.daysAgo")}</p>
      </div>
    </div>
  );
}

// ─── Right Sidebar cards ──────────────────────────────────────
interface RsvpData {
  status?: string;
  headcount?: number;
  diet?: string;
}

// Shared glass-card class string for sidebar cards
const glassCard = "bg-card dark:bg-[rgba(27,24,23,0.75)] dark:backdrop-blur-xl border border-border dark:border-primary/20 shadow-sm dark:shadow-xl rounded-xl";

// Hoisted — stable across renders, no need to recreate per component instance
const STATUS_STYLE = {
  yes:   { icon: CheckCircle2, iconColor: "text-emerald-500", iconBg: "bg-emerald-500/5 border border-emerald-500/30",  badgeBg: "bg-emerald-500/10 border border-emerald-500/20",  labelKey: "rsvp.statusYes",   labelColor: "text-emerald-600 dark:text-emerald-400" },
  no:    { icon: XCircle,      iconColor: "text-red-500",     iconBg: "bg-red-500/5 border border-red-500/30",          badgeBg: "bg-red-500/10 border border-red-500/20",          labelKey: "rsvp.statusNo",    labelColor: "text-red-600 dark:text-red-400"     },
  maybe: { icon: HelpCircle,   iconColor: "text-amber-500",   iconBg: "bg-amber-500/5 border border-amber-500/30",      badgeBg: "bg-amber-500/10 border border-amber-500/20",      labelKey: "rsvp.statusMaybe", labelColor: "text-amber-600 dark:text-amber-400" },
} as const;

function RsvpStatusCard({ rsvpData, slug }: { rsvpData: RsvpData | null; slug: string }) {
  const { t } = useTranslation();
  const cfg = rsvpData?.status ? STATUS_STYLE[rsvpData.status as keyof typeof STATUS_STYLE] : null;

  return (
    <div className={cn(glassCard, "p-6 space-y-5")}>
      <h3 className="text-xl font-serif font-bold">{t("home.rsvpStatus")}</h3>

      {cfg ? (
        <>
          <div className={cn("flex items-center gap-4 p-4 rounded-xl", cfg.badgeBg)}>
            <div className={cn("w-10 h-10 rounded-full flex items-center justify-center shrink-0", cfg.iconBg)}>
              <cfg.icon className={cn("h-5 w-5", cfg.iconColor)} />
            </div>
            <div>
              <p className={cn("text-sm font-bold", cfg.labelColor)}>{t(cfg.labelKey)}</p>
              {!!rsvpData?.headcount && rsvpData.status !== "no" && (
                <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-medium mt-0.5">
                  {t("home.partyOf", { count: rsvpData.headcount })}
                </p>
              )}
            </div>
          </div>
          <AppLink
            to={paths.wedding.rsvp(slug)}
            className="block w-full py-3 px-4 rounded-lg bg-primary/10 hover:bg-primary/20 text-primary text-xs font-bold text-center transition-colors border border-primary/20"
          >
            {t("home.updateRsvp")}
          </AppLink>
        </>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{t("home.rsvpNotResponded")}</p>
          <AppLink
            to={paths.wedding.rsvp(slug)}
            className="flex items-center justify-center gap-1.5 w-full text-sm font-semibold text-white bg-primary hover:bg-primary/90 rounded-lg py-3 transition-colors"
          >
            {t("home.respondNow")}
            <ChevronRight className="h-4 w-4" />
          </AppLink>
        </div>
      )}
    </div>
  );
}

function WeddingDetailsCard({
  wedding,
}: {
  wedding: {
    date_time: string | null; location: string | null;
    event2_date_time?: string | null; event2_location?: string | null;
    event2_label?: string | null; event2_label_custom?: string | null;
  };
}) {
  const { t, i18n } = useTranslation();
  const eventName = useEventName();
  // İkinci etkinlik varsa misafir İKİSİNİ DE görmeli — burada "en yakın"
  // seçilmez, hepsi kronolojik sırayla listelenir.
  const events = listWeddingEvents(eventSourceFromRow(wedding));
  // Mekan kartı + harita TEK-değer başlık yüzeyi → geri sayımın hedeflediği
  // AKTİF etkinliği gösterir (join sayfasıyla aynı `headlineEvent`). İki
  // tarihli düğünde ilk tarih geçince kendiliğinden ikinci etkinliğe döner;
  // tarih listesi (yukarıda) yine iki etkinliği de gösterir.
  const { location: venueLocation } = headlineEvent(eventSourceFromRow(wedding));
  if (!wedding.date_time && !wedding.location && events.length === 0) return null;

  return (
    <div className={cn(glassCard, "p-6 space-y-6")}>
      <h3 className="text-xl font-serif font-bold">{t("home.weddingDetails")}</h3>

      <div className="space-y-5">
        {events.map((ev) => (
          <div key={ev.which} className="flex gap-4">
            <div className="w-10 h-10 rounded-full bg-primary/20 border border-primary/20 flex items-center justify-center shrink-0">
              <CalendarDays className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground dark:text-primary/80 mb-0.5">
                {events.length > 1 ? eventName(ev) : t("home.dateTime")}
              </p>
              <p className="text-sm font-semibold leading-snug">
                {formatWeddingDate(ev.dateTime, i18n.language, WEDDING_DATE_LONG)}
              </p>
              {events.length > 1 && ev.location && (
                <p className="text-xs text-muted-foreground mt-0.5">{ev.location}</p>
              )}
            </div>
          </div>
        ))}

        {venueLocation && (
          <div className="flex gap-4">
            <div className="w-10 h-10 rounded-full bg-primary/20 border border-primary/20 flex items-center justify-center shrink-0">
              <MapPin className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground dark:text-primary/80 mb-0.5">
                {t("home.venue")}
              </p>
              <p className="text-sm font-semibold">{venueLocation}</p>
            </div>
          </div>
        )}

        {/* Map placeholder */}
        {venueLocation && (
          <button
            onClick={() => window.open(`https://maps.google.com/?q=${encodeURIComponent(venueLocation)}`, "_blank", "noopener,noreferrer")}
            className="relative w-full h-40 rounded-xl overflow-hidden cursor-pointer group border border-border dark:border-primary/10 block"
          >
            <div className="absolute inset-0 bg-secondary" />
            <div
              className="absolute inset-0 opacity-20 dark:opacity-30"
              style={{
                backgroundImage: "linear-gradient(rgba(141,107,94,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(141,107,94,0.8) 1px, transparent 1px)",
                backgroundSize: "20px 20px",
              }}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex items-center gap-2 px-5 py-2.5 bg-card/90 dark:bg-[#1b1817]/80 backdrop-blur-md text-foreground border border-border dark:border-primary/30 text-xs font-bold rounded-full shadow-lg group-hover:shadow-xl transition-shadow">
                <MapPin className="h-3.5 w-3.5 text-primary" />
                Open in Maps
              </div>
            </div>
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Activity type icon ───────────────────────────────────────
function ActivityTypeIcon({ type }: { type: ActivityItem["type"] }) {
  if (type === "like") return (
    <div className="h-9 w-9 shrink-0 rounded-full bg-rose-100 dark:bg-rose-500/20 flex items-center justify-center">
      <Heart className="h-4 w-4 text-rose-500" />
    </div>
  );
  if (type === "comment") return (
    <div className="h-9 w-9 shrink-0 rounded-full bg-sky-100 dark:bg-sky-500/20 flex items-center justify-center">
      <MessageCircle className="h-4 w-4 text-sky-500" />
    </div>
  );
  return (
    <div className="h-9 w-9 shrink-0 rounded-full bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center">
      <Upload className="h-4 w-4 text-emerald-500" />
    </div>
  );
}

// ─── Media card (shared between mobile scroll + desktop grid) ─
function MediaCard({
  item,
  className,
  onClick,
}: {
  item: TopLikedItem;
  className?: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn("relative overflow-hidden rounded-lg group cursor-pointer", className)}
    >
      {item.kind === "image" ? (
        <img
          src={cdnUrl(item.r2_key, { w: 800 })}
          alt=""
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 motion-reduce:transition-none motion-reduce:group-hover:scale-100"
          loading="lazy"
        />
      ) : (
        <>
          <video
            src={`/cdn/${item.r2_key}`}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 motion-reduce:transition-none motion-reduce:group-hover:scale-100"
            preload="metadata"
            muted
            playsInline
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/80">
              <Play className="h-4 w-4 fill-black text-black" />
            </div>
          </div>
        </>
      )}
      {item.like_count > 0 && (
        <div className="absolute bottom-3 left-3 flex items-center gap-1 bg-black/40 backdrop-blur-md px-2.5 py-1 rounded-full">
          <Heart className="h-3 w-3 fill-red-400 text-red-400 shrink-0" />
          <span className="text-white text-xs font-bold">{item.like_count}</span>
        </div>
      )}
    </button>
  );
}

// ─── Page ─────────────────────────────────────────────────────
export function HomePage() {
  const { t, i18n } = useTranslation();
  const { slug, wedding } = useWedding();
  // Geri sayımın hedefi: en yakın gelecekteki etkinlik (kına → sonra düğün).
  const activeEvent = useActiveEvent(wedding);
  const { guest } = useAuth();
  const api = useApi();
  const navigate = useAppNavigate();
  const { activityUnread: unreadCount } = useActivityBadge();

  const { data: rsvpResult, isSuccess: rsvpLoaded } = useQuery({
    queryKey: queryKeys.wedding.rsvp(slug),
    queryFn: () => api.get<RsvpData | null>("/rsvp"),
    staleTime: staleTimes.rsvp,
    enabled: !!slug,
  });
  const rsvpData = rsvpResult?.data ?? null;

  const { data: topLikedData, isLoading: topLikedLoading, isError: topLikedError } = useQuery({
    queryKey: queryKeys.media.topLiked(slug),
    queryFn: () => getTopLiked(slug),
    staleTime: staleTimes.media,
    enabled: !!slug,
  });
  const topLiked = topLikedData ?? [];

  const { data: activityData, isLoading: activityLoading } = useQuery({
    queryKey: queryKeys.wedding.activity(slug),
    queryFn: () => getActivity(slug, "all"),
    staleTime: staleTimes.activity,
    enabled: !!slug,
  });
  const recentActivity = (activityData?.data ?? []).slice(0, 8);

  const contentLoaded = !topLikedLoading && !activityLoading;
  const contentError  = topLikedError;

  const showRsvpNudge = rsvpLoaded && !rsvpData?.status;

  return (
    <div className="lg:flex lg:gap-8 lg:items-start">
      {/* ── Primary column ── */}
      <div className="space-y-5 flex-1 min-w-0">

        {/* Countdown — en yakın gelecekteki etkinliğe sayar */}
        {activeEvent && (
          <WeddingCountdown event={activeEvent} title={wedding!.title} bgCountdown={wedding!.bg_countdown} />
        )}

        {/* RSVP nudge — mobile/tablet only */}
        {showRsvpNudge && (
          <section className="lg:hidden rounded-xl bg-secondary border border-border p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
              <Mail className="h-4 w-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm text-foreground">{t("home.rsvpNudgeTitle")}</h3>
              <p className="text-xs text-muted-foreground mt-0.5">{t("home.rsvpNudgeText")}</p>
              <AppLink
                to={paths.wedding.rsvp(slug)}
                className="mt-1.5 inline-flex items-center gap-1 text-xs font-bold text-primary"
              >
                {t("home.rsvpNudgeRespond")} <ChevronRight className="h-3 w-3" />
              </AppLink>
            </div>
          </section>
        )}

        {/* Activity pill */}
        {unreadCount > 0 && (
          <button
            onClick={() => navigate(paths.wedding.gallery(slug))}
            className="flex w-full items-center justify-between rounded-full bg-primary px-5 py-3 shadow-lg shadow-primary/20 transition-opacity hover:opacity-90 cursor-pointer"
          >
            <div className="flex items-center gap-3">
              <Images className="h-5 w-5 text-white shrink-0" />
              <span className="text-white text-sm font-semibold">
                {t("home.newActivity", { count: unreadCount })}
              </span>
            </div>
            <ChevronRight className="h-4 w-4 text-white shrink-0" />
          </button>
        )}

        {/* ── Most Loved + Recent Activity — side by side on desktop ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-start">

          {/* Most Loved */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-serif font-bold text-lg">{t("home.topLiked")}</h2>
              {topLiked.length > 0 && (
                <AppLink
                  to={paths.wedding.gallery(slug)}
                  className="flex items-center gap-1 text-xs font-bold text-primary bg-primary/10 hover:bg-primary/20 px-3 py-1 rounded-full transition-colors"
                >
                  {t("home.viewGallery")} <ChevronRight className="h-3 w-3" />
                </AppLink>
              )}
            </div>

            {!contentLoaded ? (
              /* Loading skeleton */
              <div className="grid grid-cols-2 gap-2">
                {[0, 1, 2, 3].map(i => (
                  <div key={i} className="aspect-square rounded-xl bg-muted animate-pulse" />
                ))}
              </div>
            ) : contentError ? (
              <div className="flex items-center gap-2 rounded-xl border border-destructive/20 bg-destructive/5 px-4 py-3">
                <p className="text-sm text-destructive">{t("common.error")}</p>
              </div>
            ) : topLiked.length === 0 ? (
              /* Blur placeholder — no media yet */
              <div className="relative overflow-hidden rounded-xl">
                <div className="grid grid-cols-2 gap-2 pointer-events-none select-none blur-sm opacity-40">
                  {[0, 1, 2, 3].map(i => (
                    <div key={i} className="aspect-square rounded-xl bg-muted" />
                  ))}
                </div>
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-center px-4">
                  <Images className="h-7 w-7 text-muted-foreground mb-1" />
                  <p className="text-sm font-semibold text-foreground">{t("home.noMediaYet")}</p>
                  <p className="text-xs text-muted-foreground">{t("home.noMediaYetHint")}</p>
                </div>
              </div>
            ) : (
              <>
                {/* Mobile: horizontal scroll — max 4 */}
                <div className="md:hidden flex gap-3 overflow-x-auto pb-3 -mx-4 px-4 no-scrollbar">
                  {topLiked.slice(0, 4).map((item) => (
                    <MediaCard
                      key={item.id}
                      item={item}
                      className="shrink-0 w-52 h-64"
                      onClick={() => navigate(paths.wedding.gallery(slug), { state: { openMediaId: item.id } })}
                    />
                  ))}
                </div>
                {/* Desktop: 2×2 grid */}
                <div className="hidden md:grid grid-cols-2 gap-2">
                  {topLiked.slice(0, 4).map((item) => (
                    <MediaCard
                      key={item.id}
                      item={item}
                      className="aspect-square"
                      onClick={() => navigate(paths.wedding.gallery(slug), { state: { openMediaId: item.id } })}
                    />
                  ))}
                </div>
              </>
            )}
          </section>

          {/* Recent Activity */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-serif font-bold text-lg">{t("home.recentActivity")}</h2>
            </div>

            {!contentLoaded ? (
              /* Loading skeleton */
              <div className="space-y-0.5">
                {[0, 1, 2, 3].map(i => (
                  <div key={i} className="flex items-center gap-3 px-2 py-2.5 rounded-lg">
                    <div className="h-9 w-9 rounded-full bg-muted animate-pulse shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3 rounded bg-muted animate-pulse" />
                      <div className="h-2.5 rounded bg-muted animate-pulse w-2/3" />
                    </div>
                  </div>
                ))}
              </div>
            ) : recentActivity.length === 0 ? (
              /* Blur placeholder — no activity yet */
              <div className="relative overflow-hidden rounded-xl">
                <div className="space-y-0.5 pointer-events-none select-none blur-sm opacity-40">
                  {[0, 1, 2, 3].map(i => (
                    <div key={i} className="flex items-center gap-3 px-2 py-2.5 rounded-lg">
                      <div className="h-9 w-9 rounded-full bg-primary/10 shrink-0" />
                      <div className="flex-1 space-y-2">
                        <div className="h-3 rounded bg-muted" style={{ width: `${60 + i * 10}%` }} />
                        <div className="h-2.5 rounded bg-muted w-1/2" />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-center px-4">
                  <p className="text-sm font-semibold text-foreground">{t("home.noActivityYet")}</p>
                  <p className="text-xs text-muted-foreground">{t("home.noMediaYetHint")}</p>
                </div>
              </div>
            ) : (
              <div className="space-y-0.5">
                {recentActivity.map((item) => {
                    const isMe = !!guest?.id && item.guest_id === guest.id;
                    const ctx  = item.media_kind === "video" ? "video" : undefined;
                    return (
                      <button
                        key={item.id}
                        onClick={() => navigate(paths.wedding.gallery(slug), { state: { openMediaId: item.media_id } })}
                        className="flex w-full items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-primary/5 transition-colors text-left cursor-pointer"
                      >
                        <ActivityTypeIcon type={item.type} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm leading-snug">
                            {isMe
                              ? <span className="font-semibold text-primary">{t("activity.you")} </span>
                              : <span className="font-semibold">{item.guest_name} </span>
                            }
                            {/* Ternary BİLEREK t()'nin İÇİNDE: ara değişkene alınırsa
                                `pnpm check:i18n-keys` argümanı Identifier görüp atlar.
                                Burada her dal literal olduğu için ALTISI da doğrulanır. */}
                            <span className="text-muted-foreground">
                              {t(
                                item.type === "upload" || item.type === "upload_pending"
                                  ? isMe ? "activity.youUploadedOne" : "activity.uploaded"
                                  : item.type === "like"
                                    ? isMe ? "activity.youLikedOne" : "activity.liked"
                                    : isMe ? "activity.youCommentedOne" : "activity.commented",
                                { context: ctx }
                              )}
                            </span>
                          </p>
                          {item.content && (
                            <p className="truncate text-xs text-muted-foreground mt-0.5">"{item.content}"</p>
                          )}
                          <p className="text-[10px] text-muted-foreground/60 uppercase font-medium mt-0.5">
                            {formatRelativeTime(item.created_at, i18n.language)}
                          </p>
                        </div>
                        {item.media_r2_key && (
                          <div className="h-11 w-11 shrink-0 overflow-hidden rounded-md bg-muted">
                            {item.media_kind === "image" ? (
                              <img
                                src={cdnUrl(item.media_r2_key, { w: 480 })}
                                alt=""
                                width={44}
                                height={44}
                                className="h-full w-full object-cover"
                                loading="lazy"
                              />
                            ) : (
                              <div className="relative h-full w-full bg-zinc-900">
                                <video
                                  src={`/cdn/${item.media_r2_key}`}
                                  className="h-full w-full object-cover"
                                  preload="metadata"
                                  muted
                                  playsInline
                                />
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <Play className="h-3 w-3 text-white/80" />
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </section>
          </div>

      </div>

      {/* ── Desktop right sidebar ── */}
      <div className="hidden lg:flex lg:flex-col gap-6 w-96 xl:w-[26rem] shrink-0">
        <RsvpStatusCard rsvpData={rsvpData} slug={slug} />
        {wedding && <WeddingDetailsCard wedding={wedding} />}
      </div>
    </div>
  );
}
